import { collection, query, where, getDocs, updateDoc, doc, addDoc } from 'firebase/firestore';
import { db } from './config';
import { scheduleExceptionService } from './scheduleExceptionService';

// Constantes
const MAX_SIMULTANEOUS_TABLES = 4;
export const RESERVATION_DURATION = 90; // 1 hora y 30 minutos

// Funciones auxiliares
export const timeToMinutes = (timeStr) => {
  const [hours, minutes] = timeStr.split(':').map(Number);
  return hours * 60 + minutes;
};

export const minutesToTime = (minutes) => {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`;
};

export const checkTimeOverlap = (start1, end1, start2, end2) => {
  const start1Mins = timeToMinutes(start1);
  const end1Mins = timeToMinutes(end1);
  const start2Mins = timeToMinutes(start2);
  const end2Mins = timeToMinutes(end2);

  return start1Mins < end2Mins && end1Mins > start2Mins;
};

// Horarios del restaurante (por defecto)
const restaurantSchedule = {
  monday: {
    active: true,
    shifts: [
      { start: '13:00', end: '16:00' },  // Comida
      { start: '20:00', end: '22:30' }   // Cena
    ]
  },
  tuesday: {
    active: true,
    shifts: [
      { start: '13:00', end: '16:00' },  // Comida
      { start: '20:00', end: '22:30' }   // Cena
    ]
  },
  wednesday: {
    active: true,
    shifts: [
      { start: '13:00', end: '16:00' },  // Comida
      { start: '20:00', end: '22:30' }   // Cena
    ]
  },
  thursday: {
    active: true,
    shifts: [
      { start: '13:00', end: '16:00' },
      { start: '20:00', end: '22:30' }
    ]
  },
  friday: {
    active: true,
    shifts: [
      { start: '13:00', end: '16:00' },
      { start: '20:00', end: '22:30' }
    ]
  },
  saturday: {
    active: true,
    shifts: [
      { start: '13:00', end: '16:30' },  // Horario comida extendido
      { start: '20:00', end: '22:30' }
    ]
  },
  sunday: {
    active: true,
    shifts: [
      { start: '13:00', end: '16:30' },   // Solo comidas
      { start: '20:00', end: '22:30' }
    ]
  }
};

// Función para obtener el horario de un día específico (incluyendo excepciones desde Firebase)
const getDaySchedule = async (date) => {
  const dateObj = new Date(date);
  const formattedDate = dateObj.toLocaleDateString('es-ES', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });

  try {
    // Verificar si hay una excepción en Firebase para esta fecha específica
    const exception = await scheduleExceptionService.getExceptionForDate(formattedDate);
    
    if (exception) {
      console.log(`[TableService] Aplicando excepción para ${formattedDate}: ${exception.reason}`);
      return {
        active: exception.shifts.length > 0,
        shifts: exception.shifts,
        exception: exception.reason,
        customMessage: exception.message || exception.reason,
        closureType: exception.closureType
      };
    }
  } catch (error) {
    console.error('[TableService] Error al consultar excepciones:', error);
    // Si hay error consultando Firebase, continuar con horario normal
  }

  // Si no hay excepción, usar el horario normal
  const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  return restaurantSchedule[days[dateObj.getDay()]];
};

// Obtener reservas existentes para una fecha
const getExistingReservations = async (date) => {
  try {
    const reservationsRef = collection(db, 'reservations');
    const q = query(
      reservationsRef,
      where('date', '==', date),
      where('status', 'in', ['RESERVA CONFIRMADA', 'PENDIENTE DE CONFIRMACION', 'SENTADA'])
    );
    
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error obteniendo reservas:', error);
    return [];
  }
};

// Función para obtener los horarios disponibles (ACTUALIZADA)
export const getAvailableTimeSlots = async (date, requiredTables) => {
  try {
    console.log('[TableService] Obteniendo horarios para fecha:', date, 'mesas necesarias:', requiredTables);
    
    const dateObj = new Date(date);
    const daySchedule = await getDaySchedule(date); // Ahora es async

    if (!daySchedule.active) {
      console.log('[TableService] Día no activo');
      
      // Si hay excepción, devolver información sobre el cierre
      if (daySchedule.exception) {
        return [{
          time: null,
          available: false,
          availableTables: 0,
          maxPeople: 0,
          exception: daySchedule.exception,
          customMessage: daySchedule.customMessage,
          closureType: daySchedule.closureType,
          closed: true
        }];
      }
      
      return [];
    }

    const formattedDate = dateObj.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });

    const existingReservations = await getExistingReservations(formattedDate);
    let timeSlots = [];
    const now = new Date();
    const isToday = dateObj.toDateString() === now.toDateString();
    
    for (const shift of daySchedule.shifts) {
      const [startHours, startMinutes] = shift.start.split(':').map(Number);
      const [endHours, endMinutes] = shift.end.split(':').map(Number);
      
      let currentTime = new Date(dateObj);
      currentTime.setHours(startHours, startMinutes, 0, 0);
      
      const endTime = new Date(dateObj);
      endTime.setHours(endHours, endMinutes, 0, 0);

      while (currentTime <= endTime) {
        const currentTimeStr = `${String(currentTime.getHours()).padStart(2, '0')}:${String(currentTime.getMinutes()).padStart(2, '0')}`;
        
        // Para el día actual, solo mostrar horarios futuros con margen de 30 minutos
        const isAvailableTime = !isToday || currentTime > new Date(now.getTime() + 30 * 60000);
        
        if (isAvailableTime) {
          // Contar las mesas ocupadas específicamente en este horario
          const tablesInUse = existingReservations.reduce((count, res) => {
            // Solo contar si la reserva es exactamente en este horario
            if (res.time === currentTimeStr) {
              return count + (res.tables ? res.tables.length : 1);
            }
            return count;
          }, 0);

          const remainingTables = MAX_SIMULTANEOUS_TABLES - tablesInUse;
          
          timeSlots.push({
            time: currentTimeStr,
            available: remainingTables >= requiredTables,
            availableTables: remainingTables,
            maxPeople: remainingTables * 3,
            // Añadir información de la excepción si existe
            exception: daySchedule.exception || null,
            customMessage: daySchedule.customMessage || null,
            closureType: daySchedule.closureType || null
          });
        }

        currentTime.setMinutes(currentTime.getMinutes() + 15);
      }
    }

    console.log('[TableService] Horarios calculados:', timeSlots);
    return timeSlots;
  } catch (error) {
    console.error('[TableService] Error calculando horarios:', error);
    return [];
  }
};

// Función para verificar si una fecha tiene excepciones (NUEVA)
export const getDateException = async (date) => {
  const dateObj = new Date(date);
  const formattedDate = dateObj.toLocaleDateString('es-ES', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
  
  try {
    return await scheduleExceptionService.getExceptionForDate(formattedDate);
  } catch (error) {
    console.error('[TableService] Error al obtener excepción:', error);
    return null;
  }
};

// Función para verificar si el restaurante está disponible en una fecha (NUEVA)
export const isRestaurantOpenOnDate = async (date) => {
  try {
    const daySchedule = await getDaySchedule(date);
    return {
      isOpen: daySchedule.active,
      shifts: daySchedule.shifts || [],
      exception: daySchedule.exception || null,
      customMessage: daySchedule.customMessage || null,
      closureType: daySchedule.closureType || null
    };
  } catch (error) {
    console.error('[TableService] Error verificando disponibilidad:', error);
    return {
      isOpen: false,
      shifts: [],
      exception: 'Error al verificar disponibilidad',
      customMessage: 'No se pudo verificar la disponibilidad. Inténtelo más tarde.',
      closureType: null
    };
  }
};

// Obtener mesas disponibles para un horario específico (sin cambios)
export const getAvailableTables = async (date, time) => {
  try {
    console.log('[TableService] Consultando mesas para:', date, time);
    
    // Convertir la fecha al formato correcto (dd/mm/yyyy)
    const formattedDate = new Date(date).toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });

    // Obtener reservas existentes para ese día
    const existingReservations = await getExistingReservations(formattedDate);
    const requestedEndTime = minutesToTime(timeToMinutes(time) + RESERVATION_DURATION);

    // Obtener todas las mesas
    const tablesRef = collection(db, 'tables');
    const querySnapshot = await getDocs(tablesRef);
    
    // Filtrar mesas disponibles
    const availableTables = querySnapshot.docs
      .map(doc => ({
        id: doc.id,
        ...doc.data()
      }))
      .filter(table => {
        // Verificar si la mesa está ocupada en el horario solicitado
        const tableIsOccupied = existingReservations.some(reservation => {
          // Si esta reserva tiene esta mesa
          if (reservation.tables && reservation.tables.includes(table.id)) {
            // Calcular el tiempo de fin de la reserva existente
            const reservationEndTime = minutesToTime(timeToMinutes(reservation.time) + RESERVATION_DURATION);
            
            // Verificar si hay solapamiento
            return checkTimeOverlap(
              time, requestedEndTime,
              reservation.time, reservationEndTime
            );
          }
          return false;
        });

        return !tableIsOccupied;
      });

    console.log('[TableService] Mesas disponibles:', availableTables);
    return availableTables;
  } catch (error) {
    console.error('[TableService] Error al obtener mesas:', error);
    throw error;
  }
};

// Crear una reserva (sin cambios)
export const reserveTable = async (tableId, reservedUntil) => {
  try {
    const tableRef = doc(db, 'tables', tableId);
    await updateDoc(tableRef, {
      reservedUntil: reservedUntil
    });
    return true;
  } catch (error) {
    console.error('[TableService] Error al reservar mesa:', error);
    throw error;
  }
};

// Exportar el horario del restaurante (sin cambios)
export const getRestaurantSchedule = () => restaurantSchedule;